import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";

/**
 * CellInput — Spreadsheet-like input that doesn't fight the user.
 * 
 * - Local state while typing — parent only updated on blur or Enter
 * - Auto-selects all content on focus (first keystroke replaces value)
 * - Escape reverts to original value
 * - Drop-in replacement for <Input> — same event interface
 */
export function CellInput({ value: externalValue, onChange, onBlur, type = "text", ...props }) {
  const [localValue, setLocalValue] = useState(() => externalValue ?? "");
  const editingRef = useRef(false);
  const justFocusedRef = useRef(false);

  // Sync from parent only when NOT actively editing
  useEffect(() => {
    if (!editingRef.current) {
      setLocalValue(externalValue ?? "");
    }
  }, [externalValue]);

  const commit = useCallback((e) => {
    editingRef.current = false;
    justFocusedRef.current = false;
    const current = String(localValue);
    const original = String(externalValue ?? "");
    if (current !== original) {
      const syntheticEvent = { target: { value: current, type } };
      onChange?.(syntheticEvent);
    }
    onBlur?.(e);
  }, [localValue, externalValue, onChange, onBlur, type]);

  const handleFocus = useCallback(() => {
    editingRef.current = true;
    justFocusedRef.current = true;
  }, []);

  // Select all on the first click/mouseup after gaining focus
  // This is more reliable than selecting on focus because the browser
  // positions the cursor on mousedown, and we override it on mouseup
  const handleMouseUp = useCallback((e) => {
    if (justFocusedRef.current) {
      justFocusedRef.current = false;
      e.target.select();
    }
  }, []);

  // Also handle keyboard focus (Tab into field) — select after a tick
  const handleKeyUp = useCallback((e) => {
    if (justFocusedRef.current && e.key === "Tab") {
      justFocusedRef.current = false;
      e.target.select();
    }
  }, []);

  const handleBlur = useCallback((e) => {
    commit(e);
  }, [commit]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
    if (e.key === "Escape") {
      editingRef.current = false;
      justFocusedRef.current = false;
      setLocalValue(externalValue ?? "");
      setTimeout(() => e.target?.blur(), 0);
    }
  }, [externalValue]);

  const handleChange = useCallback((e) => {
    setLocalValue(e.target.value);
  }, []);

  return (
    <Input
      type={type}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onMouseUp={handleMouseUp}
      onKeyUp={handleKeyUp}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
}
